/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useState } from 'react';

// Mantine
import {
	Box,
	Text,
	Highlight,
	Group,
	ActionIcon,
} from '@mantine/core';

// Styles
import classes from './Step4.module.css';

// Components
import { Datatable } from '@components/organisms/Datatable';

// Datattable config
import { tableSchemaBase as columns } from './step4-productions.columns';

import {
	formatDate,
	handleOpenTransactionExternalLink,
} from '@utils/functions';
import dayjs from 'dayjs';

// Apis
import { useSites } from 'shared/api/get-sites';

// Store
import useStore from '@store/index';
import { MintStatus } from '@components/atoms/MintStatus';
import { TProduction } from '@global-types/index';

import {
	getLabelFromValue,
	getMintStatusColor,
} from '@utils/mint-status';
import { useMintHistoryProductions } from '@features/sourcing/mint-history/hooks/useMintHistoryProductions';
import { useTranslation } from 'react-i18next';
import { IconExternalLink } from '@tabler/icons-react';
import { NOT_APPLICABLE } from '@config/constants/common';
import clsx from 'clsx';
import { DrawerProduction } from '@features/sourcing/productions/components/DrawerProduction';
import sum from 'lodash/sum';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isArray from 'lodash/isArray';
import { formatNumeric } from '@utils/formats';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const Step4 = () => {
	/* ********************************** Hooks ********************************* */
	const [openDrawer, setOpenDrawer] = useState(false);

	const { t } = useTranslation();

	const {
		productionStartMintsSelectedRecords,
		productionStartMintsSortStatus,
		setProductionsStartMintsSelectedRow,
		productionStartMintsSelectedRow,
		mintHistorySelected,
	} = useStore();
	const [
		updateSitesList,
		generateTableFromSitesAndMinthistories,
	] = useMintHistoryProductions();

	/* ******************************** VARIABLES ******************************* */

	const TotalTokenEquivalents = isArray(
		productionStartMintsSelectedRecords
	)
		? sum(
				productionStartMintsSelectedRecords.map(
					(item) => {
						if (isNil(item.numericTokenEquivalent)) {
							return 0;
						}
						return item.numericTokenEquivalent;
					}
				)
			)
		: 0;

	/* ******************************** APIs ******************************* */
	const { data: sites } = useSites({
		_id: {
			$in:
				mintHistorySelected &&
				mintHistorySelected.productions &&
				Array.from(
					new Set(
						mintHistorySelected.productions.map(
							(item: any) => item.site
						)
					)
				),
		},
	});

	const handleOpenURL = () => {
		if (mintHistorySelected?.transactionHash)
			handleOpenTransactionExternalLink(
				mintHistorySelected?.transactionHash || ''
			);
	};

	/* ******************************** RENDERING ******************************* */

	// From mint history list
	if (
		mintHistorySelected &&
		Object.keys(mintHistorySelected).length !== 0
	)
		return (
			<Box className={classes.container}>
				{/*Drawer production */}
				<DrawerProduction
					opened={openDrawer}
					handleverify={() => {}}
					handledelete={() => {}}
					handlestartmint={() => {}}
					onClose={() => {
						setOpenDrawer(false);
					}}
					position={'right'}
					data={productionStartMintsSelectedRow}
					fromstepper
				/>

				<Box className={classes.content}>
					<Highlight
						highlight={[
							'highlighted',
							'' + isNumber(TotalTokenEquivalents)
								? formatNumeric(TotalTokenEquivalents)
								: '0',
						]}
						highlightStyles={{
							backgroundImage:
								'linear-gradient(45deg, #647FE8, #647FE8)',
							fontWeight: 700,
							WebkitBackgroundClip: 'text',
							WebkitTextFillColor: 'transparent',
						}}
						c="var(--mantine-color-dark)"
						fw={500}
						className={classes.title}
					>
						{productionStartMintsSelectedRecords.length +
							` ${t(
								'productions.productionsMintedWithAnEquivalentOf'
							)} ${
								TotalTokenEquivalents &&
								isNumber(TotalTokenEquivalents)
									? formatNumeric(TotalTokenEquivalents)
									: 0
							} tCO2e `}
					</Highlight>
					<Box
						className={classes.containerMintInformations}
					>
						<Box className={classes.colMintInformation}>
							<Text
								className={classes.mintInformartionTitle}
							>
								{t('global.mintDate')}
							</Text>
							<Text
								className={classes.mintInformartionDate}
							>
								{mintHistorySelected &&
								mintHistorySelected.createdAt &&
								dayjs(
									mintHistorySelected.createdAt
								).isValid()
									? `${formatDate(
											mintHistorySelected.createdAt,
											'DD/MM/YYYY'
										)}`
									: `${formatDate(
											dayjs(),
											'DD/MM/YYYY'
										)}`}
							</Text>
						</Box>
						<Box
							className={classes.borderColMintInformation}
						/>
						<Box className={classes.colMintInformation}>
							<Text
								className={classes.mintInformartionTitle}
							>
								{t('global.mintStatus')}
							</Text>

							{mintHistorySelected &&
							mintHistorySelected.status ? (
								<MintStatus
									children={getLabelFromValue(
										mintHistorySelected &&
											mintHistorySelected.status &&
											mintHistorySelected.status
									)}
									type={getMintStatusColor(
										mintHistorySelected &&
											mintHistorySelected.status &&
											mintHistorySelected.status
									)}
								/>
							) : (
								<Text className={classes.notApplicable}>
									{NOT_APPLICABLE}
								</Text>
							)}
						</Box>
						<Box
							className={classes.borderColMintInformation}
						/>
						<Box className={classes.colMintInformation}>
							<Group gap={'xs'}>
								<Text
									className={
										classes.mintInformartionTitle
									}
								>
									{`${t('global.blockchain')} :`}
								</Text>
								<Text
									className={
										classes.mintInformartionTitle
									}
								>
									{mintHistorySelected.blockchain ||
										NOT_APPLICABLE}
								</Text>
							</Group>
							<Group gap={'xs'}>
								<Text
									className={
										classes.mintInformartionLabelTokenId
									}
								>
									{`${t('global.tokenId')}`}
								</Text>
								{mintHistorySelected?.transactionHash && (
									<ActionIcon
										size={'xs'}
										onClick={() => {}}
										variant="transparent"
									>
										<IconExternalLink
											onClick={handleOpenURL}
											className={
												classes.mintInformartionTokenId
											}
											size={'15px'}
										/>
									</ActionIcon>
								)}
								<Text
									className={clsx(
										classes.mintInformartionTokenId,
										!mintHistorySelected?.transactionHash &&
											classes.mintInformartionTokenIdNotApplicable
									)}
									onClick={handleOpenURL}
								>
									:
									{` ${
										!mintHistorySelected?.transactionHash
											? NOT_APPLICABLE
											: mintHistorySelected.transactionHash
									}`}
								</Text>
							</Group>
						</Box>
					</Box>
					<Box className={classes.contentDatatable}>
						<Datatable
							loading={false}
							columns={columns}
							data={[]}
							onRowClick={() => {}}
							onViewMore={(row: TProduction) => {
								setProductionsStartMintsSelectedRow(row);
								setOpenDrawer(true);
							}}
							style={{
								minHeight: '100%',
							}}
							height={250}
							selectedRecords={null}
							sortstatus={productionStartMintsSortStatus}
						/>
					</Box>
				</Box>
			</Box>
		);

	// From start mint creation
	return (
		<Box className={classes.container}>
			{/*Drawer production */}
			<DrawerProduction
				opened={openDrawer}
				handleverify={() => {}}
				handledelete={() => {}}
				handlestartmint={() => {}}
				onClose={() => {
					setOpenDrawer(false);
				}}
				position={'right'}
				data={productionStartMintsSelectedRow}
				fromstepper
			/>
			<Box className={classes.content}>
				<Highlight
					highlight={[
						'highlighted',
						'' + TotalTokenEquivalents &&
						isNumber(TotalTokenEquivalents)
							? formatNumeric(TotalTokenEquivalents)
							: '0',
					]}
					highlightStyles={{
						backgroundImage:
							'linear-gradient(45deg, #647FE8, #647FE8)',
						fontWeight: 700,
						WebkitBackgroundClip: 'text',
						WebkitTextFillColor: 'transparent',
					}}
					c="var(--mantine-color-dark)"
					fw={500}
					className={classes.title}
				>
					{productionStartMintsSelectedRecords.length +
						` ${t(
							'productions.productionsReadyToMintWithAnEquivalentOf'
						)} ${
							TotalTokenEquivalents &&
							isNumber(TotalTokenEquivalents)
								? formatNumeric(TotalTokenEquivalents)
								: 0
						} tCO2e`}
				</Highlight>
				<Box
					className={classes.containerMintInformations}
				>
					<Box className={classes.colMintInformation}>
						<Text
							className={classes.mintInformartionTitle}
						>
							{t('global.mintDate')}
						</Text>
						<Text
							className={classes.mintInformartionDate}
						>
							{`${formatDate(dayjs(), 'DD/MM/YYYY')}`}
						</Text>
					</Box>
					<Box
						className={classes.borderColMintInformation}
					/>
					<Box className={classes.colMintInformation}>
						<Text
							className={classes.mintInformartionTitle}
						>
							{t('global.mintStatus')}
						</Text>
						<MintStatus
							children={t('In progress')}
							type={'warning'}
						/>
					</Box>
				</Box>
				<Box className={classes.contentDatatable}>
					<Datatable
						loading={false}
						columns={columns}
						data={productionStartMintsSelectedRecords}
						onRowClick={() => {}}
						onViewMore={(row: TProduction) => {
							setProductionsStartMintsSelectedRow(row);
							setOpenDrawer(true);
						}}
						style={{
							minHeight: '100%',
						}}
						height={250}
						selectedRecords={null}
						sortstatus={productionStartMintsSortStatus}
					/>
				</Box>
			</Box>
		</Box>
	);
};
